import React, { useState } from 'react';
import { Button, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useAuthStore } from '../../store/authStore';
import { refreshExtensionStatus, useExtensionStatus } from '../../hooks/useExtensionStatus';
import { PrefillOnOpen, usePrefillOnOpen } from '../../hooks/usePrefillOnOpen';
import { connectExtension } from '../../services/extensionBridge';
import { errorMessage } from '../../utils/errors';

const LABEL_ID = 'prefill-on-open-label';

/**
 * The "Prefill on open" preference, for signed-in users: whether opening a
 * job's application from the dashboard has the extension fill the form as it
 * loads. Says what is missing when the extension is not installed or not
 * connected to this account.
 */
const PrefillOnOpenControl: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const status = useExtensionStatus();
  const [value, setValue] = usePrefillOnOpen();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  // Until the extension has answered, assume it is there: no flash of a warning.
  const installed = status ? status.installed : true;
  const connected = status ? status.authenticated && status.userId === user.id : true;

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      // Publishes the new status, which this component follows.
      const next = await connectExtension(user.id, true);
      if (!next.authenticated) setError('The extension did not accept the sign-in. Try again.');
    } catch (e) {
      setError(errorMessage(e, 'Could not connect the extension'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 1.5 }} data-testid="prefill-on-open">
      <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.75 }}>
        <Typography id={LABEL_ID} variant="subtitle2" component="span">
          Prefill on open
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={value}
          disabled={!installed}
          onChange={(_, next: PrefillOnOpen | null) => {
            // Clicking the pressed button gives null; keep the choice instead.
            if (next !== null) setValue(next);
          }}
          aria-labelledby={LABEL_ID}
          sx={{
            maxWidth: '100%',
            '& .MuiToggleButton-root': { textTransform: 'none', lineHeight: 1.2, py: 0.5, px: 1.25, fontSize: '0.8rem' },
          }}
        >
          <ToggleButton value="off">Off</ToggleButton>
          <ToggleButton value="fill">Prefill</ToggleButton>
          <ToggleButton value="tailored">Prefill with tailored resume</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 260px', minWidth: 0 }}>
          {value === 'tailored'
            ? 'When you open a job\'s application, JobFlo fills it in, then builds a resume tailored to that job and attaches it. It never submits.'
            : 'When you open a job\'s application, JobFlo fills it in automatically. It never submits.'}
        </Typography>
      </Stack>

      {!installed ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Install the JobFlo extension to use this.
          </Typography>
          <Button size="small" onClick={refreshExtensionStatus}>
            Check again
          </Button>
        </Stack>
      ) : (
        !connected && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
            <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
              {error || 'Connect the extension to use this.'}
            </Typography>
            <Button size="small" onClick={connect} disabled={busy}>
              {busy ? 'Connecting…' : 'Connect extension'}
            </Button>
          </Stack>
        )
      )}
    </Paper>
  );
};

export default PrefillOnOpenControl;
